import { ReactNode, useMemo } from "react";
import { FiX } from "react-icons/fi";
import ReactSelect, { Props as ReactSelectProps } from "react-select";
import makeAnimated from "react-select/animated";
import ReactTooltip from "react-tooltip";
import FormikFieldError from "../FormikFieldError";
import { createSingleSelectCustomTestIdComponents } from "utils/selectHelper";
import { getReactSelectClassNames, getInputWrapperClasses } from "./inputStyles";
import LabelWithGuideline from "../LabelWithGuideline";
import { Spinner } from "@chakra-ui/react";

const animatedComponents = makeAnimated();

export interface SelectOption {
  label: string;
  value: any;
}

export interface NodeSelectOption {
  label: ReactNode;
  value: any;
}

export interface BaseSelectWithLabelProps extends Omit<ReactSelectProps, 'onChange' | 'value'> {
  name?: string;
  className?: string;
  clearAble?: boolean;
  disabled?: boolean;
  errorText?: string;
  guideLine?: string;
  hasError?: boolean;
  height?: string;
  isGuideLineLink?: boolean;
  isLoading?: boolean;
  isMulti?: boolean;
  label?: string;
  noLabel?: boolean;
  options: SelectOption[] | NodeSelectOption[];
  placeholder?: string;
  required?: boolean;
  testId?: string;
  value?: SelectOption | SelectOption[] | NodeSelectOption | NodeSelectOption[] | null | number;
  width?: string;
  noOptionsMessage?: (obj: { inputValue: string }) => React.ReactNode;
  onChange?: (option: SelectOption | SelectOption[] | null) => void;
  onGuideLineClick?: () => void;
  onMenuOpen?: () => void;
}

const loadingOptions = [{ label: <>Loading... <Spinner size={"xs"} /></>, value: "" }];

export const SelectWithLabel: React.FC<BaseSelectWithLabelProps> = ({
  className = "",
  clearAble,
  disabled,
  errorText,
  guideLine,
  isGuideLineLink,
  hasError = false,
  height,
  isLoading,
  isMulti = false,
  label,
  name,
  noLabel,
  noOptionsMessage,
  onBlur,
  onChange,
  onInputChange,
  onMenuOpen,
  onGuideLineClick,
  options,
  placeholder,
  required,
  testId,
  value,
  width,
}) => {
  const hasInputError = !!errorText || hasError;

  const mergedComponents = useMemo(() => ({
    ...animatedComponents,
    ...(testId ? createSingleSelectCustomTestIdComponents(testId) : {}),
  }), [testId]);

  const selectedValue = typeof value === 'number' ? options?.find((option) => option.value === value) : value;

  const hasValue = Array.isArray(selectedValue) ? selectedValue.length > 0 : !!(selectedValue && (selectedValue as SelectOption)?.value);

  const handleOnChange = (selected: any) => {
    if (!onChange) {
      return;
    }
    if (isMulti) {
      onChange([...(selected as readonly SelectOption[])]);
    } else {
      onChange(selected as SelectOption | null);
    }
  };

  const menuPortalStyle = (base: any) => ({
    ...base,
    zIndex: 9999,
  });

  return (
    <div
      className={getInputWrapperClasses(hasInputError, false, className)}
      data-testid={testId}
      style={{ width }}
    >
      {!noLabel && <LabelWithGuideline label={label} htmlFor={name} guideLine={guideLine} isGuideLineLink={isGuideLineLink} required={required} onGuideLineClick={onGuideLineClick} />}

      <div className="relative">
        <ReactSelect
          name={name}
          unstyled
          classNames={getReactSelectClassNames(hasInputError, hasValue, disabled, height, isMulti)}
          closeMenuOnSelect={!isMulti}
          components={mergedComponents}
          isMulti={isMulti}
          onChange={handleOnChange}
          onInputChange={onInputChange}
          value={selectedValue}
          options={isLoading ? loadingOptions : options}
          isDisabled={disabled}
          placeholder={isLoading ? "Loading..." : placeholder}
          onBlur={onBlur}
          onMenuOpen={onMenuOpen}
          isLoading={isLoading}
          {...(noOptionsMessage ? { noOptionsMessage } : {})}
          menuPortalTarget={document.body}
          styles={{ menuPortal: menuPortalStyle }}
        />

        {clearAble && selectedValue && !isMulti && (
          <>
            <FiX
              className="absolute right-[60px] top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600"
              data-tip
              data-for={`clear-${name}-tooltip`}
              size={16}
              onClick={() => handleOnChange(null)}
            />
            <ReactTooltip id={`clear-${name}-tooltip`}>
              Clear Value
            </ReactTooltip>
          </>
        )}
      </div>

      <FormikFieldError text={errorText} />
    </div>
  );
};
